import { useState } from "react";

import { Check, Loader2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useApproveLicenseRequest,
  useRejectLicenseRequest,
} from "@/features/licenses/hooks/useLicenses";
import type { ILicenseRequest } from "@/features/licenses/schemas/license";
import { softwareTypeLabel } from "@/features/servers/schemas/servers";
import { cn } from "@/shared/lib/utils";

function formatDate(value?: string): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("es", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

const STATUS_STYLES: Record<ILicenseRequest["status"], string> = {
  pending:
    "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200",
  approved:
    "bg-green-100 text-green-900 dark:bg-green-900/30 dark:text-green-200",
  rejected: "bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200",
};

interface LicenseRequestRowProps {
  request: ILicenseRequest;
  institutionName?: string;
  compact?: boolean;
}

/**
 * Fila de solicitud con acciones aprobar / rechazar (solo si pending).
 */
export function LicenseRequestRow({
  request,
  institutionName,
  compact = false,
}: LicenseRequestRowProps) {
  const approve = useApproveLicenseRequest();
  const reject = useRejectLicenseRequest();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");

  const busy = approve.isPending || reject.isPending;
  const isPending = request.status === "pending";

  const handleApprove = () => {
    const ok = window.confirm(
      `¿Aprobar licencia ${softwareTypeLabel(request.softwareType)} para "${request.clientName}" (${request.machineId})?`
    );
    if (!ok) return;
    approve.mutate(request.id);
  };

  const handleReject = () => {
    reject.mutate(
      { requestId: request.id, reason: reason.trim() || undefined },
      {
        onSuccess: () => {
          setRejectOpen(false);
          setReason("");
        },
      }
    );
  };

  return (
    <div
      className={cn(
        "rounded-lg border p-3 space-y-2",
        compact && "p-2.5 space-y-1.5"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-0.5">
          <p className="font-medium text-sm truncate">
            {softwareTypeLabel(request.softwareType)}
            <span className="text-muted-foreground font-normal">
              {" · "}
              {request.clientName || "Sin nombre"}
            </span>
          </p>
          {institutionName && institutionName !== request.clientName ? (
            <p className="text-xs text-muted-foreground truncate">
              Usuario: {institutionName}
            </p>
          ) : null}
          <p className="text-xs text-muted-foreground font-mono truncate">
            {request.machineId}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {formatDate(request.createdAt)}
          </p>
        </div>
        <span
          className={cn(
            "shrink-0 inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium capitalize",
            STATUS_STYLES[request.status]
          )}
        >
          {request.status}
        </span>
      </div>

      {isPending && !rejectOpen && (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            className="cursor-pointer"
            onClick={handleApprove}
            disabled={busy}
          >
            {approve.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" aria-hidden />
            ) : (
              <Check className="h-3.5 w-3.5 mr-1.5" aria-hidden />
            )}
            Aprobar
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="cursor-pointer"
            onClick={() => setRejectOpen(true)}
            disabled={busy}
          >
            <X className="h-3.5 w-3.5 mr-1.5" aria-hidden />
            Rechazar
          </Button>
        </div>
      )}

      {isPending && rejectOpen && (
        <div className="space-y-2 rounded-md border bg-muted/20 p-2">
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Motivo (opcional)"
            aria-label="Motivo de rechazo"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="destructive"
              className="cursor-pointer"
              onClick={handleReject}
              disabled={busy}
            >
              {reject.isPending && (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" aria-hidden />
              )}
              Confirmar rechazo
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="cursor-pointer"
              onClick={() => {
                setRejectOpen(false);
                setReason("");
              }}
              disabled={busy}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {request.status === "approved" && request.licenseId ? (
        <p className="text-[11px] text-muted-foreground font-mono">
          licenseId: {request.licenseId}
        </p>
      ) : null}
      {request.status === "rejected" && request.reason ? (
        <p className="text-[11px] text-muted-foreground">
          Motivo: {request.reason}
        </p>
      ) : null}
    </div>
  );
}
